import Component from '@glimmer/component'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SeriesColumn, type SeriesColumnOptions } from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type { IntlService } from 'ember-intl'
import { service } from '@ember/service'
import { formatNumber } from 'teacher-ui-client/utils/giraffe/formatter'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import { t } from 'ember-intl'
import { hash } from '@ember/helper'
import { colors } from '@colors'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import fullGradeName from 'teacher-ui-client/helpers/full-grade-name'
import type { TotalsReportDataForGrade } from 'teacher-ui-client/components/reporting/writing-legends/course-progress/totals/report-loader'

interface Signature {
  Args: {
    totalsReportDataForGrade: TotalsReportDataForGrade
    scopeName: UIScopeName
  }
}

export class CourseProgressTotalsReport extends Component<Signature> {
  @service intl!: IntlService

  get summaryStats(): Stat[] {
    const summaryData = this.args.totalsReportDataForGrade.summary
    return [
      {
        label: this.intl.t('reporting.statBoxes.courseProgress.lessonsCompleted'),
        value: formatNumber(summaryData.total_count ?? 0, 1),
      },
      {
        label: this.intl.t('reporting.statBoxes.courseProgress.studentCount'),
        value: formatNumber(summaryData.student_count ?? 0, 1),
      },
      {
        label: this.intl.t('reporting.statBoxes.courseProgress.perStudentCount'),
        value: formatNumber(summaryData.student_average ?? 0, 1),
      },
    ]
  }

  get summaryBoxTitle() {
    switch (this.args.scopeName) {
      case 'teacher':
        return this.intl.t('reporting.summaryTitles.courseProgressTotals.teacher')
      case 'school-class':
        return this.intl.t('reporting.summaryTitles.courseProgressTotals.schoolClass')
      case 'school':
        return this.intl.t('reporting.summaryTitles.courseProgressTotals.school')
      default:
        return ''
    }
  }

  get monthCounts() {
    return this.args.totalsReportDataForGrade.months.map(({ count }) => count)
  }

  /**
   * Formats month data for Highcharts.
   */
  get columns(): SeriesColumnOptions[] {
    return [{ name: 'count', data: this.monthCounts, color: colors.oceanyBlue325, type: 'column' }]
  }

  /**
   * Formats month data for Highcharts for use as column labels.
   * Converts month dates into short names.
   */
  get categories() {
    return this.args.totalsReportDataForGrade.months.map(({ month }) => convertDateFormat(month))
  }

  <template>
    <LeftSplitChart>
      <:left>
        <SummaryBox
          class="h-full"
          @title={{this.summaryBoxTitle}}
          @grade={{fullGradeName @totalsReportDataForGrade.grade_position}}
          @stats={{this.summaryStats}}
          @colorScheme={{SummaryBoxColorScheme.WritingLegendsBlue}}
        />
      </:left>
      <:right>
        <SeriesColumn
          class="pt-2 pb-4"
          @title={{hash text=(t "reporting.chartTitles.courseProgressTotals")}}
          @data={{this.columns}}
          @categories={{this.categories}}
          @stackingStyle="normal"
          @yAxisLabel={{t "reporting.yAxisLabels.numberOfLessons"}}
        />
      </:right>
    </LeftSplitChart>
  </template>
}
